import * as React from "react";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { Box, IconButton, Tooltip } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { apiUrls } from "../../actprep-ui/utils/urls";
import api from "../../actprep-ui/utils/api";
import {
  sortingModes,
  sexyBooleanColumn,
} from "../../actprep-ui/constants/dataGrid";
import MasterRowDetailsModal from "../MasterRowDetailsModal";

/**
 * Props:
 *  - startDate: ISO string "YYYY-MM-DD" for the week start.
 *  - pageSizeOptions?: number[] (optional)
 *  - defaultExpandAll?: boolean (optional, default=false)
 */
export default function MasterSheetV2({
  startDate,
  pageSizeOptions = [25, 50, 100],
  defaultExpandAll = false,
}) {
  const apiRef = useGridApiRef();
  const [modalState, setModalState] = React.useState({
    open: false,
    rowType: null,
    rowId: null,
  });

  const handleOpenDetails = React.useCallback((row) => {
    setModalState({
      open: true,
      rowType: row.row_type,
      rowId: row.id,
    });
  }, []);

  const handleCloseModal = React.useCallback(() => {
    setModalState({
      open: false,
      rowType: null,
      rowId: null,
    });
  }, []);

  // --- columns shared by role & student rows ---
  const columns = React.useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1.2,
        minWidth: 240,
      },
      { field: "role", headerName: "Role", width: 160 },
      {
        field: "hours",
        headerName: "Hours %",
        type: "number",
        width: 110,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            return sexyBooleanColumn(params);
          }
          return params.value;
        },
      },
      {
        field: "memorization",
        headerName: "Memorization %",
        type: "number",
        width: 140,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            return sexyBooleanColumn(params);
          }
          return params.value;
        },
      },
      {
        field: "check_ins",
        headerName: "Check-ins %",
        type: "number",
        width: 120,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            return sexyBooleanColumn(params);
          }
          return params.value;
        },
      },
      {
        field: "score",
        headerName: "Score %",
        type: "number",
        width: 110,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            return sexyBooleanColumn(params);
          }
          return params.value;
        },
      },
      {
        field: "accuracy",
        headerName: "Accuracy %",
        type: "number",
        width: 120,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            return sexyBooleanColumn(params);
          }
          return params.value;
        },
      },
      {
        field: "hours_volunteers",
        headerName: "Hours Vol. %",
        type: "number",
        width: 140,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            if (params.row.is_hours_volunteer) {
              return params.value;
            }
            return null;
          }
          return params.value;
        },
      },
      {
        field: "max_increase",
        headerName: "Max Increase",
        width: 180,
        // string on role rows, number on student rows — display as-is
      },
      {
        field: "itg_percent",
        headerName: "ITG %",
        type: "number",
        width: 100,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            return null;
          }
          return params.value;
        },
      },
      {
        field: "average",
        headerName: "Average %",
        type: "number",
        width: 120,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            return null;
          }
          return params.value;
        },
      },
      {
        field: "total_increase_this_week",
        headerName: "Total Increase This Week",
        type: "number",
        width: 180,
      },
      {
        field: "english_increase_this_week",
        headerName: "English Increase",
        type: "number",
        width: 140,
      },
      {
        field: "math_increase_this_week",
        headerName: "Math Increase",
        type: "number",
        width: 130,
      },
      {
        field: "reading_increase_this_week",
        headerName: "Reading Increase",
        type: "number",
        width: 140,
      },
      {
        field: "science_increase_this_week",
        headerName: "Science Increase",
        type: "number",
        width: 140,
      },
      {
        field: "on_track_foundation",
        headerName: "On Track Foundation %",
        type: "number",
        width: 160,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            return sexyBooleanColumn(params);
          }
          return params.value;
        },
      },
      {
        field: "on_track_persistence",
        headerName: "On Track Persistence %",
        type: "number",
        width: 160,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            return sexyBooleanColumn(params);
          }
          return params.value;
        },
      },
      {
        field: "on_track_determination",
        headerName: "On Track Determination %",
        type: "number",
        width: 170,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            return sexyBooleanColumn(params);
          }
          return params.value;
        },
      },
      {
        field: "on_track_accuracy",
        headerName: "On Track Accuracy %",
        type: "number",
        width: 150,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            return sexyBooleanColumn(params);
          }
          return params.value;
        },
      },
      {
        field: "on_track_gold_award",
        headerName: "On Track Gold Award %",
        type: "number",
        width: 160,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            return sexyBooleanColumn(params);
          }
          return params.value;
        },
      },
      {
        field: "on_track_letter",
        headerName: "On Track Letter %",
        type: "number",
        width: 150,
        renderCell: (params) => {
          if (params.row.row_type === "student") {
            return sexyBooleanColumn(params);
          }
          return params.value;
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="View Details">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDetails(params.row);
              }}
              color="primary"
            >
              <InfoIcon />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [handleOpenDetails]
  );

  // --- DataSource implementation per MUI X server-side tree data ---
  // Docs: dataSource + getRows + getGroupKey + getChildrenCount
  // https://mui.com/x/react-data-grid/server-side-data/tree-data/
  const dataSource = React.useMemo(() => {
    /** @type {import('@mui/x-data-grid-pro').GridDataSource} */
    const ds = {
      /**
       * params.groupKeys is the path of group keys for the expanded node.
       * We'll map it to the path of RA UUIDs (ra_ids) for our backend.
       */
      getRows: async (params) => {
        // If the grid asks for the top level (groupKeys = []), we'll use the provided rootRaId as our path.
        const groupKeys =
          params.groupKeys && params.groupKeys.length > 0
            ? params.groupKeys
            : ["top"];

        // Build request params. Your server parses ra_ids as a comma list.
        const filteredGroupKeys = groupKeys.filter((key) => key !== "top");
        const r = await api.get(apiUrls.admin.masterV2(), {
          params: {
            start_date: startDate,
            ra_ids: filteredGroupKeys.join(","), // server uses parse_param("ra_ids", param_type="list")
          },
        });

        const rows = (r.data?.data ?? []).map((row) => ({
          // DataGridPro row id
          id: row.id,
          // Required fields for tree server-side mode:
          // - We'll use each row.id as its own group key when it's expandable.
          // - Row count for children is provided by backend as count_children.
          ...row,
        }));

        // rowCount is optional (useful when you paginate); not needed here.
        return { rows };
      },

      /**
       * Return the group key for a given row.
       * For role rows we key by their UUID (row.id) so expanding a role requests children with [...path, row.id].
       * Student rows always have count_children = 0, so they won't be expanded.
       */
      getGroupKey: (row) => {
        // If you want only role rows to be expandable, return undefined for students.
        // But the Data Source expects a key; we'll still return id, and the grid
        // won't ask for children when getChildrenCount(row) === 0.
        return row.id;
      },

      getGroupLabel: (row) => {
        return row.name;
      },

      /**
       * Tell the grid how many children a row has.
       * - Role rows: backend gives `count_children`
       * - Student rows: always 0
       */
      getChildrenCount: (row) => {
        if (typeof row.count_children === "number") return row.count_children;
        return 0;
      },
    };

    return ds;
  }, [startDate]);

  return (
    <Box
      sx={{ height: "75vh", display: "flex", flexDirection: "column", gap: 1 }}
    >
      {/* <Toolbar
        variant="dense"
        sx={{ px: 0.5, justifyContent: "space-between" }}
      >
        <Typography variant="h6">Master Sheet (Hierarchy)</Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Typography variant="body2">Week starting: {startDate}</Typography>
          <Button size="small" onClick={handleResetCache}>
            Reset cache
          </Button>
        </Box>
      </Toolbar> */}

      <DataGridPro
        apiRef={apiRef}
        columns={columns}
        // Server-side tree data hook-up
        dataSource={dataSource}
        treeData
        // Expand behavior. Set -1 to expand all by default; else 0 for collapsed.
        defaultGroupingExpansionDepth={defaultExpandAll ? -1 : 0}
        // Cosmetic/UX
        disableRowSelectionOnClick
        groupingColDef={{
          //   valueFormatter: (params, row) => {
          //     return row?.name;
          //   },
          headerName: "Team",
          valueGetter: (params, row) => {
            return row?.label;
          },
        }}
        pinnedColumns={{
          right: ["actions"],
        }}
        sortingMode={sortingModes.CLIENT}
        showCellVerticalBorder={false}
        showColumnVerticalBorder={false}
        pagination={false}
        pageSizeOptions={pageSizeOptions}
        // Optional—keep density compact for dashboards
        density="compact"
      />

      <MasterRowDetailsModal
        open={modalState.open}
        onClose={handleCloseModal}
        rowType={modalState.rowType}
        rowId={modalState.rowId}
        startDate={startDate}
      />
    </Box>
  );
}
